import { useState, useEffect } from 'react';
import { Copy } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { MoneyInput } from '../common/MoneyInput';
import { canonicalMoney } from '../../lib/money';
import { useEditTeamMember } from '../../hooks/useEditTeamMember';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import type { TeamMember } from '../../types';

interface Props { member: TeamMember | null; onClose: () => void; onUpdated: () => void; }

const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

export function EditTeamMemberPanel({ member, onClose, onUpdated }: Props) {
  const business = useAuthStore(s => s.business);
  const currentUser = useAuthStore(s => s.user);
  const { updateTeamMember, deactivateTeamMember, reactivateTeamMember, loading, error } = useEditTeamMember();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [empType, setEmpType] = useState('employee');
  const [role, setRole] = useState('employee');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [userCode, setUserCode] = useState<string | null>(null);
  const [prefLang, setPrefLang] = useState('en');
  const [codeCopied, setCodeCopied] = useState(false);

  const isOwnerRow = member?.role === 'owner';
  const isSelf = member?.id === currentUser?.id;
  const isInactive = member?.status === 'inactive';

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!member) return;
    setFirstName(member.firstName); setLastName(member.lastName);
    setEmpType(member.employmentType); setRole(member.role);
    setPhone(member.phone ?? ''); setEmail(member.email ?? '');
    setHourlyRate(member.hourlyRate != null ? canonicalMoney(member.hourlyRate) : '');
    // Fetch extra fields not in TeamMember view-model
    void supabase.from('users').select('user_code, preferred_language').eq('id', member.id).maybeSingle()
      .then(({ data }) => {
        setUserCode(data?.user_code ?? null);
        setPrefLang(data?.preferred_language ?? 'en');
      });
  }, [member]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSave = async () => {
    if (!member || !business || !currentUser) return;
    const ok = await updateTeamMember(member.id, business.id, currentUser.id, {
      first_name: firstName, last_name: lastName,
      role: isOwnerRow ? member.role : role,
      employment_type: empType,
      phone: phone || null, email: email || null,
      // Q1.5 boundary: updateTeamMember accepts hourly_rate as number.
      // hourlyRate is canonical "25.00" after MoneyInput's blur.
      hourly_rate: hourlyRate ? Number(hourlyRate) : null,
      preferred_language: prefLang,
    }, `Updated ${firstName} ${lastName}`);
    if (ok) { onUpdated(); onClose(); }
  };

  const handleDeactivate = async () => {
    if (!member || !business || !currentUser) return;
    const ok = await deactivateTeamMember(member.id, business.id, currentUser.id, `Deactivated ${member.firstName} ${member.lastName}`);
    if (ok) { setConfirmOpen(false); onUpdated(); onClose(); }
  };

  const handleReactivate = async () => {
    if (!member || !business || !currentUser) return;
    const ok = await reactivateTeamMember(member.id, business.id, currentUser.id, `Reactivated ${member.firstName} ${member.lastName}`);
    if (ok) { onUpdated(); onClose(); }
  };

  return (
    <SlidePanel isOpen={!!member} onClose={onClose} title="Edit Team Member" subtitle={member ? `${member.firstName} ${member.lastName}` : ''}>
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}

      {isInactive && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.amberWash, borderLeft: `3px solid ${palette.amber}`, color: palette.amber, fontSize: 12.5, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>This member is inactive.</span>
          <button onClick={handleReactivate} disabled={loading} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: palette.green, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Reactivate</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, ...FIELD }}>
        <div style={{ flex: 1 }}><label style={LABEL}>First name *</label><input style={INPUT} value={firstName} onChange={e => setFirstName(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Last name *</label><input style={INPUT} value={lastName} onChange={e => setLastName(e.target.value)} /></div>
      </div>
      <div style={FIELD}><label style={LABEL}>Type</label><div style={{ display: 'flex', gap: 16 }}>{['employee', 'contractor', 'operator'].map(t => <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}><input type="radio" name="empType" checked={empType === t} onChange={() => setEmpType(t)} /><span style={{ textTransform: 'capitalize' }}>{t}</span></label>)}</div></div>
      {currentUser?.role === 'owner' && !isOwnerRow && (
        <div style={FIELD}><label style={LABEL}>Role</label><select style={INPUT} value={role} onChange={e => setRole(e.target.value)}><option value="employee">Employee</option><option value="manager">Manager</option></select></div>
      )}
      <div style={FIELD}><label style={LABEL}>Phone</label><input style={INPUT} value={phone} onChange={e => setPhone(e.target.value)} /></div>
      <div style={FIELD}><label style={LABEL}>Email</label><input type="email" style={INPUT} value={email} onChange={e => setEmail(e.target.value)} /></div>
      <div style={FIELD}><label style={LABEL}>Hourly rate</label><MoneyInput value={hourlyRate} onChange={setHourlyRate} /></div>

      {/* User code + Language */}
      <div style={{ display: 'flex', gap: 12, ...FIELD }}>
        {userCode && (
          <div style={{ flex: 1 }}>
            <label style={LABEL}>User code</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: palette.g800, letterSpacing: '0.05em' }}>{userCode}</span>
              <button onClick={() => { void navigator.clipboard.writeText(userCode); setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }} style={{ padding: '3px 8px', borderRadius: 4, border: `1px solid ${palette.g200}`, background: palette.white, cursor: 'pointer', fontSize: 11, color: codeCopied ? palette.green : palette.g500, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Copy size={11} /> {codeCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <label style={LABEL}>Preferred language</label>
          <select style={INPUT} value={prefLang} onChange={e => setPrefLang(e.target.value)}>
            <option value="en">English</option>
            <option value="fr">Francais</option>
            <option value="ru">Pусский</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${palette.g100}`, marginTop: 8 }}>
        {!isOwnerRow && !isSelf && !isInactive && (
          <button onClick={() => setConfirmOpen(true)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: palette.red, cursor: 'pointer', fontFamily: 'inherit' }}>Deactivate</button>
        )}
        {(isOwnerRow || isSelf || isInactive) && <div />}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: loading ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{loading ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeactivate} title={`Deactivate ${member?.firstName}?`} message="They will lose access to the app and won't appear in active team lists." confirmLabel="Deactivate" loading={loading} />
    </SlidePanel>
  );
}
